import { useEffect, useState } from "react";
import { Award, Users, Home, TrendingUp, Star } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const About = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  return (
    <div data-testid="about-page">
      <section className="bg-primary text-primary-foreground py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6">About Paul Murray</h1>
          <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto leading-relaxed">
            Your trusted partner in Greater Toronto Area real estate for over 15 years
          </p>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1592878995758-02b32ddabdd3?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Paul Murray"
                className="w-full h-[600px] object-cover shadow-lg"
                data-testid="agent-portrait"
              />
            </div>
            <div>
              <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">My Story</p>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
                Dedicated to Excellence
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  For over 15 years, I've had the privilege of helping families throughout the Greater Toronto Area find their perfect homes and achieve their real estate dreams. My passion for real estate stems from a genuine love of connecting people with properties that truly fit their lifestyle and aspirations.
                </p>
                <p>
                  Born and raised in Toronto, I have an intimate knowledge of the GTA's diverse neighborhoods, market trends, and hidden gems. This deep-rooted connection to the area, combined with my extensive industry experience, allows me to provide unparalleled insights and guidance to my clients.
                </p>
                <p>
                  My approach is simple: listen carefully, communicate clearly, and work tirelessly to exceed expectations. Whether you're a first-time buyer, seasoned investor, or looking to sell your property, I'm committed to making your real estate journey smooth, successful, and stress-free.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Proven Track Record</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              Results That Speak For Themselves
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-8 bg-white" data-testid="stat-experience">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Award className="w-8 h-8 text-accent" />
              </div>
              <p className="text-5xl font-heading font-bold text-primary mb-2">15+</p>
              <p className="text-sm uppercase tracking-widest text-muted-foreground">Years Experience</p>
            </div>
            <div className="text-center p-8 bg-white" data-testid="stat-clients">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Users className="w-8 h-8 text-accent" />
              </div>
              <p className="text-5xl font-heading font-bold text-primary mb-2">500+</p>
              <p className="text-sm uppercase tracking-widest text-muted-foreground">Happy Clients</p>
            </div>
            <div className="text-center p-8 bg-white" data-testid="stat-properties">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <Home className="w-8 h-8 text-accent" />
              </div>
              <p className="text-5xl font-heading font-bold text-primary mb-2">750+</p>
              <p className="text-sm uppercase tracking-widest text-muted-foreground">Properties Sold</p>
            </div>
            <div className="text-center p-8 bg-white" data-testid="stat-sales">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 mb-6">
                <TrendingUp className="w-8 h-8 text-accent" />
              </div>
              <p className="text-5xl font-heading font-bold text-primary mb-2">$200M+</p>
              <p className="text-sm uppercase tracking-widest text-muted-foreground">Total Sales Volume</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">My Approach</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              Client-Focused Service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="w-12 h-1 bg-accent mb-6"></div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Expert Guidance</h3>
              <p className="text-muted-foreground leading-relaxed">
                With deep knowledge of the GTA market, I provide insights and advice that help you make informed decisions with confidence.
              </p>
            </div>
            <div>
              <div className="w-12 h-1 bg-accent mb-6"></div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Personalized Attention</h3>
              <p className="text-muted-foreground leading-relaxed">
                Every client is unique. I take the time to understand your specific needs, preferences, and goals to deliver tailored solutions.
              </p>
            </div>
            <div>
              <div className="w-12 h-1 bg-accent mb-6"></div>
              <h3 className="text-2xl font-heading font-semibold text-primary mb-4">Proven Results</h3>
              <p className="text-muted-foreground leading-relaxed">
                My track record speaks for itself. I negotiate the best deals and ensure smooth transactions from start to finish.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-accent mb-4 font-semibold">Client Testimonials</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-6">
              What My Clients Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-8 border-l-4 border-accent" data-testid={`testimonial-${testimonial.id}`}>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-accent fill-accent" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-semibold text-primary">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                  <p className="text-xs text-muted-foreground mt-1">{testimonial.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;